import axiosInstance from '@/app/api/axiosInstance';
import type {
    DuplicateWarning,
    FollowUpBucket,
    LeadActivity,
    LeadActivityPayload,
    LeadConvertPayload,
    LeadConvertResponse,
    LeadCreatePayload,
    LeadCreateResponse,
    LeadDetail,
    LeadListResponse,
    LeadSummary,
} from '@/app/types/lead';

/**
 * Every read here passes `mine=true`.
 *
 * Sales managers hold the `org_admin` role, so the backend would otherwise show
 * them the whole organisation's pipeline. `mine` is honoured independently of role
 * precisely so this app can scope itself.
 */

export const getMyLeads = async (params: {
    followUp?: FollowUpBucket;
    search?: string;
    status?: string;
    includeConverted?: boolean;
    limit?: number;
    offset?: number;
}): Promise<LeadListResponse> => {
    const query: Record<string, unknown> = {
        mine: true,
        limit: params.limit ?? 100,
        skip: params.offset ?? 0,
        sort_by: 'next_follow_up_date',
        sort_dir: 'asc',
    };
    if (params.followUp) query.follow_up = params.followUp;
    if (params.search) query.search_query = params.search;
    if (params.status) query.status = params.status;
    if (params.includeConverted) query.include_converted = true;

    const response = await axiosInstance.get<LeadListResponse>('/leads/', { params: query });
    return response.data;
};

export const getMyLeadSummary = async (): Promise<LeadSummary> => {
    const response = await axiosInstance.get<LeadSummary>('/leads/summary', {
        params: { mine: true },
    });
    return response.data;
};

export const getLeadById = async (leadId: number): Promise<LeadDetail> => {
    const response = await axiosInstance.get<LeadDetail>(`/leads/${leadId}`);
    return response.data;
};

export const createLead = async (payload: LeadCreatePayload): Promise<LeadCreateResponse> => {
    const response = await axiosInstance.post<LeadCreateResponse>('/leads/', payload);
    return response.data;
};

/**
 * Advisory duplicate lookup, called on phone blur. Returns null when nothing
 * matches. This never blocks capture — see the service docstring on the backend.
 */
export const checkDuplicatePhone = async (
    phone: string,
    excludeLeadId?: number
): Promise<DuplicateWarning | null> => {
    const params: Record<string, unknown> = { phone };
    if (excludeLeadId) params.exclude_lead_id = excludeLeadId;
    const response = await axiosInstance.get<DuplicateWarning | null>('/leads/check-duplicate', {
        params,
    });
    return response.data ?? null;
};

export const addLeadActivity = async (
    leadId: number,
    payload: LeadActivityPayload
): Promise<{ activity: LeadActivity; lead: LeadDetail }> => {
    const response = await axiosInstance.post(`/leads/${leadId}/activities`, payload);
    return response.data;
};

export const updateLeadLocation = async (leadId: number, geoLocation: string) => {
    const response = await axiosInstance.patch(`/leads/${leadId}`, { geo_location: geoLocation });
    return response.data;
};

export const convertLead = async (
    leadId: number,
    payload: LeadConvertPayload
): Promise<LeadConvertResponse> => {
    const response = await axiosInstance.post<LeadConvertResponse>(
        `/leads/${leadId}/convert`,
        payload
    );
    return response.data;
};

export interface PartyGroupOption {
    id: number;
    name: string;
}

/** Party groups for the convert sheet. LEADS is filtered out — retiring it is the point. */
export const getPartyGroups = async (): Promise<PartyGroupOption[]> => {
    const response = await axiosInstance.get('/parties/groups/', { params: { limit: 100 } });
    const items: PartyGroupOption[] = response.data?.items ?? [];
    return items.filter((g) => g.name.toUpperCase() !== 'LEADS');
};
